import { isRecord } from '@/utils/filter/isRecord';

const REQUEST_ID_HEADER = 'x-amzn-requestid';
const REQUEST_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const STATUS_LABEL_PATTERN = /^[A-Za-z ]{1,30}$/;

export type SharedViewRecordKey = 'filter' | 'filterDetails';
export type SharedViewOptionalPayloadField =
  | 'localFilters'
  | 'resolvedCountryCodes'
  | 'senderCountryCodes'
  | 'optionPaths';

const RECORD_KEYS: readonly SharedViewRecordKey[] = ['filter', 'filterDetails'];
const OPTIONAL_PAYLOAD_FIELDS: readonly SharedViewOptionalPayloadField[] = [
  'localFilters',
  'resolvedCountryCodes',
  'senderCountryCodes',
  'optionPaths',
];

export type SharedViewRecordFilterType = 'saved_filter' | 'shared_filter';

const isRecordFilterType = (value: unknown): value is SharedViewRecordFilterType =>
  value === 'saved_filter' || value === 'shared_filter';

export interface SharedViewRecordSummary {
  key: SharedViewRecordKey;
  filterType?: SharedViewRecordFilterType;
  keys: string[];
}

export interface SharedViewResponseSummary {
  statusLabel?: string;
  records: SharedViewRecordSummary[];
  filterCountriesType?: string;
}

export interface SharedViewOptionalFieldSummary {
  name: SharedViewOptionalPayloadField;
  type?: string;
}

export interface SharedViewPayloadSummary {
  envelopeKeys?: string[];
  payloadKeys: string[];
  globalFilterKeys?: string[];
  optionalFields: SharedViewOptionalFieldSummary[];
}

export const describeType = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const summarizeRecord = (key: SharedViewRecordKey, record: Record<string, unknown>): SharedViewRecordSummary => ({
  key,
  filterType: isRecordFilterType(record.filterType) ? record.filterType : undefined,
  keys: Object.keys(record),
});

export const summarizeResponse = (body: unknown): SharedViewResponseSummary | undefined => {
  if (!isRecord(body)) {
    return undefined;
  }
  const { status, filterCountries } = body;
  return {
    statusLabel: typeof status === 'string' && STATUS_LABEL_PATTERN.test(status) ? status : undefined,
    records: RECORD_KEYS.flatMap(key => {
      const candidate = body[key];
      return isRecord(candidate) ? [summarizeRecord(key, candidate)] : [];
    }),
    filterCountriesType: filterCountries === undefined ? undefined : describeType(filterCountries),
  };
};

export const summarizePayload = (rawPayload: unknown, payload: unknown): SharedViewPayloadSummary | undefined => {
  const effective = payload ?? rawPayload;
  if (!isRecord(effective)) {
    return undefined;
  }
  const hasGlobalFilters = 'globalFilters' in effective;
  const { globalFilters } = effective;
  return {
    envelopeKeys: isRecord(rawPayload) ? Object.keys(rawPayload) : undefined,
    payloadKeys: Object.keys(effective),
    globalFilterKeys: isRecord(globalFilters) ? Object.keys(globalFilters) : undefined,
    optionalFields: hasGlobalFilters
      ? OPTIONAL_PAYLOAD_FIELDS.map(name => ({
          name,
          type: effective[name] === undefined ? undefined : describeType(effective[name]),
        }))
      : [],
  };
};

export const readRequestId = (headers: unknown): string | undefined => {
  if (!isRecord(headers)) {
    return undefined;
  }
  const entry = Object.entries(headers).find(([headerName]) => headerName.toLowerCase() === REQUEST_ID_HEADER);
  const value = entry?.[1];
  return typeof value === 'string' && REQUEST_ID_PATTERN.test(value) ? value : undefined;
};

export const readErrorRequestId = (error: unknown): string | undefined =>
  isRecord(error) && isRecord(error.response) ? readRequestId(error.response.headers) : undefined;

export const extractSharedViewResponseFacts = (response: {
  data: unknown;
  headers: unknown;
}): { responseBody: unknown; requestId?: string } => ({
  responseBody: response.data,
  requestId: readRequestId(response.headers),
});
